import React, { ComponentType, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useTypedSelector } from "../../redux/store";
import {
	StyledColumn,
	StyledGrid,
	StyledGridItem,
	SuspenseLoader,
} from "../../components/common";
import { ConnectionStatus } from "../../components/ConnectionQuality";
import {
	LanguagePicker,
	useInitLoginLanguage,
	useLoginLanguage,
} from "../../components/LoginLanguage";
import { ROUTES } from "../../constants/routes";

/** A normal connect takes about a second; show the status only after that. */
const SHOW_STATUS_AFTER_MS = 3_000;

export default function RequireNotAuth(Component: ComponentType) {
	return function ComponentWithMiddleware(props: Record<string, unknown>) {
		const history = useHistory();

		const { connected, authorized } = useTypedSelector(
			(state) => state.prpc,
		);

		const [showStatus, setShowStatus] = useState(false);

		// The login screen speaks the stored / browser language; a signed-in
		// user passing through keeps theirs.
		useInitLoginLanguage(authorized !== true);
		const [language, setLanguage] = useLoginLanguage();

		useEffect(() => {
			if (connected && authorized) history.push(ROUTES.MAIN);
		}, [history, connected, authorized]);

		// Without a socket the login form never shows; tell the dispatcher
		// why the spinner keeps spinning.
		useEffect(() => {
			if (connected) {
				setShowStatus(false);
				return undefined;
			}
			const timer = setTimeout(
				() => setShowStatus(true),
				SHOW_STATUS_AFTER_MS,
			);
			return () => clearTimeout(timer);
		}, [connected]);

		const picker = authorized !== true && (
			<LanguagePicker value={language} onChange={setLanguage} />
		);

		return !connected || authorized === true ? (
			<StyledGrid areas="'main'" w="100%" h="100vh">
				{picker}
				<StyledGridItem
					area="main"
					justifySelf="center"
					alignSelf="center"
				>
					<StyledColumn gap="24px" alignItems="center">
						<SuspenseLoader position="static" />
						{showStatus && !connected && (
							<ConnectionStatus lang={language} />
						)}
					</StyledColumn>
				</StyledGridItem>
			</StyledGrid>
		) : (
			<>
				{picker}
				<Component {...props} />
			</>
		);
	};
}
